import {createContext, ReactNode, useEffect, useRef, useState} from "react";
import {useBeforeUnload, useBlocker, useSearchParams} from "react-router";

import {CheckUnsavedModal} from "@/pages/checks/update/sections/CheckUnsavedModal.tsx";

export interface TabContext {
  tab: string;
  changeTab: (tab: string) => void;
  tabBlocked: "blocked" | "unblocked";
  proceedTab: () => void;
  blockReset: () => void;
  setTabBlocker: (blocker: boolean) => void;
  showUnsavedModal: boolean;
  setShowUnsavedModal: (show: boolean) => void;
  setOnBypass: (callback: (() => void) | undefined) => void;
}

const tabContextDefaultValues: TabContext = {
  tab: "",
  changeTab: () => {},
  tabBlocked: "unblocked",
  proceedTab: () => {},
  blockReset: () => {},
  setTabBlocker: () => {},
  setShowUnsavedModal: () => {},
  showUnsavedModal: false,
  setOnBypass: () => {},
};

export const TabContext = createContext<TabContext>(tabContextDefaultValues);

interface UseTabsProps {
  defaultTab: string;
  blockerFunction?: () => boolean;
  urlTabParam?: string;
  tabList?: string[];
  preserveSearchParams?: string[];
}

const validTabOrDefault = (tab: string | null, defaultTab: string, tabList: string[]): string => {
  if (tab && tabList.includes(tab)) {
    return tab;
  }
  return defaultTab;
};

const newSearchParamsWithPreserve = (params: URLSearchParams, preserve: string[]): URLSearchParams => {
  const newParams = new URLSearchParams();
  preserve.forEach((key) => {
    const value = params.get(key);
    if (value) {
      newParams.set(key, value);
    }
  });
  return newParams;
};

export const useTabs = ({
  defaultTab,
  urlTabParam = "tab",
  tabList = [],
  preserveSearchParams = [],
}: UseTabsProps): TabContext => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<string>(validTabOrDefault(searchParams.get(urlTabParam), defaultTab, tabList));
  const [tabBlocked, setTabBlocked] = useState<"blocked" | "unblocked">("unblocked");
  const [tabLocation, setTabLocation] = useState<string | null>(null);
  const [tabBlocker, setTabBlocker] = useState<boolean>(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const onBypassRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    const urlTab = searchParams.get(urlTabParam);
    const newTab = validTabOrDefault(urlTab, defaultTab, tabList);
    if (newTab !== tab) {
      setTab(newTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab, urlTabParam, searchParams.get(urlTabParam)]);

  // Blocker is supposed to be a singleton which breaks nested tabs https://github.com/remix-run/react-router/discussions/9978
  const blocker = useBlocker(tabBlocker && !showUnsavedModal);

  const setOnBypass = (callback: (() => void) | undefined) => {
    onBypassRef.current = callback;
  };

  const changeTab = (newTab: string) => {
    if (tabBlocker) {
      setTabBlocked("blocked");
      setTabLocation(newTab);
      setShowUnsavedModal(true);
      return;
    }

    // Only the URL is written: the effect above sets `tab` in the same render as the params this navigation drops, so
    // the new tab never loads with the old tab's search or page.
    setTabLocation(null);
    setTabBlocked("unblocked");
    const params = newSearchParamsWithPreserve(searchParams, preserveSearchParams);
    params.set(urlTabParam, newTab);
    setSearchParams(params, {preventScrollReset: true});
  };

  const proceedTab = () => {
    if (onBypassRef.current) {
      onBypassRef.current();
    }

    if (tabLocation) {
      setTabLocation(null);
      setTabBlocker(false);
      setTabBlocked("unblocked");
      const params = newSearchParamsWithPreserve(searchParams, preserveSearchParams);
      params.set(urlTabParam, tabLocation);
      setSearchParams(params, {preventScrollReset: true});
    } else if (blocker.state === "blocked") {
      setTabBlocker(false);
      setTabBlocked("unblocked");
      blocker.proceed();
    } else {
      setTabBlocker(false);
      setTabBlocked("unblocked");
    }
  };

  const blockReset = () => {
    setTabBlocked("unblocked");
    setTabLocation(null);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  useEffect(() => {
    if (blocker.state === "blocked" && !showUnsavedModal) {
      setShowUnsavedModal(true);
    }
  }, [blocker.state, showUnsavedModal]);

  useBeforeUnload((event) => {
    if (tabBlocker) {
      event.preventDefault();
      return "You have unsaved changes. Are you sure you want to discard your changes?";
    }
  });

  return {
    tab,
    changeTab,
    tabBlocked,
    proceedTab,
    blockReset,
    setTabBlocker,
    showUnsavedModal,
    setShowUnsavedModal,
    setOnBypass,
  };
};

interface TabContextProviderProps extends TabContext {
  children: ReactNode;
}

export const TabContextProvider = ({children, ...rest}: TabContextProviderProps) => {
  return (
    <TabContext.Provider value={{...rest}}>
      {children}
      <CheckUnsavedModal
        show={rest.showUnsavedModal}
        onCancel={() => {
          rest.blockReset();
          rest.setShowUnsavedModal(false);
        }}
        onConfirm={() => {
          rest.proceedTab();
          rest.setShowUnsavedModal(false);
        }}
      />
    </TabContext.Provider>
  );
};
